import React, { useCallback, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useFrameClassificationAnnotation } from '../../../../common/hooks/use-frame-classification-annotation';
import { useTaskClassificationAnnotation } from '../../../../common/hooks/use-task-classification-annotation';
import { ReviewModeProvider } from '../../../../common/providers/review-mode-providers';
import { DEFAULT_REVIEW_FEATURE_CONFIG, ReviewModeSidePanel, useEditAnnotationHandlers, useSetReviewFeature } from '../../../../common/review-mode-side-panel';
import useTaskStateStore from '../../../../common/task-state/providers/hooks/useTaskStateStore';
import type { StoreTypeT } from '../../../../common/types/types';
import { STORE_TYPE } from '../../../../common/types/types';
import { useTaskLevelQuestionnaire } from '../../../../modernization-common/provider/task-level-questionnaire';
import { useSidePanel } from '../../../../modernization-common/side-panel';
import { SIDE_PANEL_ACTIONS } from '../../../../modernization-common/side-panel/const';
import { useDispatch } from '../../../../modernization-common/side-panel/hooks/use-dispatch';
import { annotationMetadataAtom } from '../../atoms/annotation-atoms';
import { allAnnotationsDataWithFrameRangesAtom, relationshipStoreWithLabelDataAtom } from '../../atoms/relationship-atoms';
import { annotationIdToSpanAtom } from '../../atoms/side-panel-atoms';
import { getReviewAnnotations } from './utils';
export const ReviewSidePanel = () => {
  const {
    atom: taskLevelQuestionAnswersAtom,
    store
  } = useTaskLevelQuestionnaire();
  const {
    annotationsAtom,
    selectedAnnotationIdsAtom
  } = useSidePanel();
  const annotations = useAtomValue(annotationsAtom);
  const annotationIdToSpanMap = useAtomValue(annotationIdToSpanAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const relationships = useAtomValue(relationshipStoreWithLabelDataAtom);
  const allAnnotations = useAtomValue(allAnnotationsDataWithFrameRangesAtom);
  const {
    setEditAnnotationCallback
  } = useEditAnnotationHandlers(true);
  const dispatch = useDispatch();
  const taskLevelQuestionAnswers = useAtomValue(taskLevelQuestionAnswersAtom, {
    store
  });
  const taskClassification = useTaskClassificationAnnotation({
    taskLevelQuestionAnswers
  });
  const getAnnotationDynamicAnswers = useCallback((id: string) => annotationMetadata.get(id)?.dynamicAnswers ?? [], [annotationMetadata]);
  const frameClassification = useFrameClassificationAnnotation({
    annotations,
    annotationIdToSpanMap,
    getAnnotationDynamicAnswers
  });
  const reviewAnnotations = useMemo(() => getReviewAnnotations(annotations, annotationIdToSpanMap, getAnnotationDynamicAnswers, relationships, allAnnotations, taskClassification, frameClassification), [annotations, annotationIdToSpanMap, getAnnotationDynamicAnswers, relationships, allAnnotations, taskClassification, frameClassification]);
  const selectedAnnotationId = selectedAnnotationIds[0];
  const setReviewFeature = useSetReviewFeature();
  const {
    reviewFeatureConfig
  } = useTaskStateStore();
  useEffect(() => {
    setReviewFeature({
      ...DEFAULT_REVIEW_FEATURE_CONFIG,
      ...reviewFeatureConfig
    });
  }, [setReviewFeature, reviewFeatureConfig]);
  useEffect(() => {
    setEditAnnotationCallback({
      onEditCallback: annotationId => {
        dispatch({
          type: SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_START,
          annotationId
        });
      },
      onInstanceDeleteCallback: undefined
    });
    return () => {
      setEditAnnotationCallback(null);
    };
  }, [setEditAnnotationCallback, dispatch]);
  const handleSelectAnnotation = useCallback((annotationId: string) => {
    dispatch({
      type: SIDE_PANEL_ACTIONS.SELECT_ANNOTATION,
      annotationId
    });
  }, [dispatch]);
  const handleOnShowHideAnnotations = (hidden: boolean, annotationId: string, storeType?: StoreTypeT) => {
    dispatch({
      type: SIDE_PANEL_ACTIONS.SHOW_HIDE_ANNOTATIONS,
      hidden,
      annotationId,
      storeType: storeType || STORE_TYPE.ANNOTATION_STORE
    });
    return;
  };
  return <ReviewModeProvider toggleAnnotationVisibility={handleOnShowHideAnnotations} selectAnnotation={handleSelectAnnotation}>
      <ReviewModeSidePanel annotations={reviewAnnotations} selectedAnnotationId={selectedAnnotationId} />
    </ReviewModeProvider>;
};